import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Search,
  MapPin,
  Trash2,
  Edit,
  Download,
  RefreshCw,
  RotateCcw,
  Check,
  X
} from "lucide-react";

const SettingsDestinos = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [destinations, setDestinations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingDestination, setEditingDestination] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [newDestination, setNewDestination] = useState({
    city: "",
    state: "",
    country: ""
  });

  useEffect(() => {
    fetchDestinations();
  }, [showInactive]);

  const fetchDestinations = async () => {
    try {
      // Always fetch all destinations and filter in frontend based on showInactive
      const res = await axios.get(`${API}/destinations/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter based on showInactive: true = only inactive, false = only active
      const filtered = showInactive 
        ? res.data.filter(d => d.is_active === false)
        : res.data.filter(d => d.is_active !== false);
      setDestinations(filtered);
    } catch (error) {
      console.error("Error fetching destinations:", error);
      toast.error("Error al cargar destinos");
    } finally {
      setLoading(false);
    }
  };

  const handleClearAndImport = async () => {
    if (!confirm("¿Estás seguro? Esto eliminará TODOS los destinos y los volverá a importar.")) {
      return;
    }
    setImporting(true);
    try {
      await axios.delete(`${API}/destinations/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.info("Destinos eliminados, importando...");
      
      const res = await axios.post(`${API}/destinations/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchDestinations();
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Error al reimportar destinos");
    } finally {
      setImporting(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await axios.post(`${API}/destinations/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      fetchDestinations();
    } catch (error) {
      console.error("Error importing destinations:", error);
      toast.error(error.response?.data?.detail || "Error al importar destinos");
    } finally {
      setImporting(false);
    }
  };

  const handleAdd = async () => {
    if (!newDestination.city || !newDestination.state || !newDestination.country) {
      toast.error("Todos los campos son requeridos");
      return;
    }
    try {
      await axios.post(`${API}/destinations`, newDestination, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Destino agregado");
      setShowAddDialog(false);
      setNewDestination({ city: "", state: "", country: "" });
      fetchDestinations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar destino");
    }
  };

  const handleEdit = async () => {
    if (!editingDestination) return;
    try {
      await axios.put(`${API}/destinations/${editingDestination.id}`, {
        city: editingDestination.city,
        state: editingDestination.state,
        country: editingDestination.country
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Destino actualizado");
      setShowEditDialog(false);
      setEditingDestination(null);
      fetchDestinations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar destino");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Inactivar este destino?")) return;
    try {
      await axios.delete(`${API}/destinations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Destino inactivado");
      fetchDestinations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al inactivar destino");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API}/destinations/restore/${id}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Destino restaurado");
      fetchDestinations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al restaurar destino");
    }
  };

  const filteredDestinations = destinations.filter(dest => {
    const search = searchTerm.toLowerCase();
    return (
      dest.city?.toLowerCase().includes(search) ||
      dest.state?.toLowerCase().includes(search) ||
      dest.country?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-screen">
        <div className="animate-pulse text-muted-foreground">Cargando destinos...</div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="settings-destinos-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-3xl font-bold text-slate-900">
                Destinos
              </h1>
              <p className="text-slate-500">Catálogo de destinos turísticos para cotizaciones</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleImport}
              disabled={importing}
            >
              <Download className="w-4 h-4 mr-2" />
              {importing ? "Procesando..." : "Importar Nuevos"}
            </Button>
            <Button
              variant="destructive"
              onClick={handleClearAndImport}
              disabled={importing}
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {importing ? "Procesando..." : "Limpiar y Reimportar"}
            </Button>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Agregar
            </Button>
          </div>
        </div>

        {/* Stats and Search */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
              <div className="flex items-center gap-4">
                <div className={`flex items-center gap-2 ${showInactive ? 'bg-red-50' : 'bg-emerald-50'} px-4 py-2 rounded-lg`}>
                  <MapPin className={`w-5 h-5 ${showInactive ? 'text-red-600' : 'text-emerald-600'}`} />
                  <span className={`font-semibold ${showInactive ? 'text-red-900' : 'text-emerald-900'}`}>{destinations.length}</span>
                  <span className={showInactive ? 'text-red-600' : 'text-emerald-600'}>
                    {showInactive ? 'destinos inactivos' : 'destinos activos'}
                  </span>
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                    className="rounded"
                  />
                  Mostrar solo inactivos
                </label>
              </div>
              <div className="relative w-full md:w-80">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por ciudad, estado, país..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="destination-search"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Destinations Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-slate-600" />
              Catálogo de Destinos
            </CardTitle>
            <CardDescription>
              Formato: Ciudad, Estado/Municipio, País
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border max-h-[600px] overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white">
                  <TableRow>
                    <TableHead>Ciudad / Localidad</TableHead>
                    <TableHead>Estado / Municipio</TableHead>
                    <TableHead>País</TableHead>
                    <TableHead className="w-[100px]">Estado</TableHead>
                    <TableHead className="w-[100px]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDestinations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                        {destinations.length === 0 
                          ? "No hay destinos. Haz clic en 'Importar Nuevos' para cargar destinos populares."
                          : "No se encontraron destinos con ese criterio de búsqueda"
                        }
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDestinations.slice(0, 200).map((dest) => (
                      <TableRow 
                        key={dest.id}
                        className={dest.is_active === false ? "opacity-50 bg-slate-50" : ""}
                      >
                        <TableCell className="font-medium">{dest.city}</TableCell>
                        <TableCell className="text-slate-600">{dest.state}</TableCell>
                        <TableCell className="text-slate-600">{dest.country}</TableCell>
                        <TableCell>
                          {dest.is_active === false ? (
                            <span className="flex items-center gap-1 text-xs text-red-600">
                              <X className="w-3 h-3" /> Inactivo
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-green-600">
                              <Check className="w-3 h-3" /> Activo
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditingDestination(dest);
                                setShowEditDialog(true);
                              }}
                              className="h-8 w-8"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {dest.is_active === false ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRestore(dest.id)}
                                className="h-8 w-8 text-green-600 hover:text-green-700"
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(dest.id)}
                                className="h-8 w-8 text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {filteredDestinations.length > 200 && (
              <p className="text-sm text-slate-500 mt-2 text-center">
                Mostrando 200 de {filteredDestinations.length} destinos. Usa el buscador para filtrar.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Add Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Agregar Destino</DialogTitle>
              <DialogDescription>
                Ingresa los datos del nuevo destino turístico
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Ciudad / Localidad *</Label>
                <Input
                  value={newDestination.city}
                  onChange={(e) => setNewDestination({...newDestination, city: e.target.value})}
                  placeholder="Cancún"
                />
              </div>
              <div className="space-y-2">
                <Label>Estado / Municipio *</Label>
                <Input
                  value={newDestination.state}
                  onChange={(e) => setNewDestination({...newDestination, state: e.target.value})}
                  placeholder="Quintana Roo"
                />
              </div>
              <div className="space-y-2">
                <Label>País *</Label>
                <Input
                  value={newDestination.country}
                  onChange={(e) => setNewDestination({...newDestination, country: e.target.value})}
                  placeholder="México"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAdd}>
                Agregar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Editar Destino</DialogTitle>
              <DialogDescription>
                Modifica los datos del destino
              </DialogDescription>
            </DialogHeader>
            {editingDestination && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Ciudad / Localidad *</Label>
                  <Input
                    value={editingDestination.city}
                    onChange={(e) => setEditingDestination({...editingDestination, city: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Estado / Municipio *</Label>
                  <Input
                    value={editingDestination.state}
                    onChange={(e) => setEditingDestination({...editingDestination, state: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>País *</Label>
                  <Input
                    value={editingDestination.country}
                    onChange={(e) => setEditingDestination({...editingDestination, country: e.target.value})}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={handleEdit}>
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SettingsDestinos;
